import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

function NoteBox({ childId, onDone }) {
  const [text, setText] = useState("");
  const [toParent, setToParent] = useState(true);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/watcher/child/${childId}/note`, { text, to_parent: toParent }); toast.success(toParent ? "Note sent to parent" : "Internal note saved"); setText(""); onDone?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 bg-slate-50 rounded-xl p-2 space-y-2" data-testid={`note-box-${childId}`}>
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a note…" className="rounded-lg text-sm" data-testid={`note-text-${childId}`} />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={toParent} onChange={(e) => setToParent(e.target.checked)} className="w-4 h-4 accent-turquoise" data-testid={`note-toparent-${childId}`} /> Send to parent</label>
        <Button size="sm" onClick={send} disabled={busy} className="rounded-full bg-navy text-white text-xs" data-testid={`note-send-${childId}`}>Send</Button>
      </div>
    </div>
  );
}

export default function Watcher() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [openNote, setOpenNote] = useState(null);
  const load = () => api.get("/watcher/today").then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const shift = data?.shift;
  const onShift = shift && !shift.clock_out;
  const clock = async (action) => { try { await api.post(`/watcher/shift/${action}`); toast.success(action === "in" ? "Clocked in" : "Clocked out"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const mark = async (id, event) => { try { await api.post(`/watcher/child/${id}/mark/${event}`); toast.success(event === "arrive" ? "Arrival logged & parent notified" : "Departure logged & parent notified"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="h-16 flex items-center justify-between gap-2 px-4 border-b border-white/10">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm hidden sm:block">EXPLORER WATCH</p></div>
        <div className="flex items-center gap-1 overflow-x-auto [&>button]:shrink-0">
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/program")} data-testid="watcher-goto-program">Program</Button>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/photos")} data-testid="watcher-goto-photos">Day Gallery</Button>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/profile")} data-testid="watcher-goto-profile">My Profile</Button>
          <Button variant="ghost" size="sm" className="text-white" onClick={() => nav("/staff/attendance")} data-testid="watcher-goto-attendance">Full Register</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }} data-testid="watcher-logout"><Icons.LogOut className="w-5 h-5" /></Button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div><h1 className="font-display font-extrabold text-xl">Hi {user?.name}</h1><p className="text-white/50 text-sm">{data?.date} · {data?.children?.length || 0} explorers assigned</p></div>
          {onShift
            ? <Button onClick={() => clock("out")} className="rounded-full bg-coral text-white font-bold" data-testid="shift-out-btn">Clock Out</Button>
            : <Button onClick={() => clock("in")} className="rounded-full bg-turquoise text-navy font-bold" data-testid="shift-in-btn">Clock In</Button>}
        </div>
        {shift && <p className="text-xs text-white/50" data-testid="shift-status">Shift: in {new Date(shift.clock_in).toLocaleTimeString()} {shift.clock_out ? `· out ${new Date(shift.clock_out).toLocaleTimeString()}` : "· on duty"}</p>}

        {(data?.children || []).length === 0 && <p className="text-white/50 bg-white/5 rounded-xl p-6 text-center" data-testid="watcher-empty">No explorers assigned to you today. Ask your admin to assign your explorers on the Assignments screen.</p>}

        {(data?.children || []).map((c) => (
          <div key={c.id} className="bg-white/5 border border-white/10 rounded-2xl p-4" data-testid={`watch-child-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-display font-bold">{c.first_name} {c.last_name} <span className="font-mono text-turquoise text-xs">{c.explorer_id}</span></p>
                <p className="text-xs text-white/50">{c.school || "—"}{c.dropoff_preference ? ` · drop-off: ${c.dropoff_preference}` : ""}</p>
                {c.parent_phone && <p className="text-xs text-white/50">Parent: {c.parent_name} · {c.parent_phone}</p>}
                <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full ${c.attendance === "present" ? "bg-emerald-500/20 text-emerald-300" : c.attendance === "checked_out" ? "bg-slate-500/20 text-slate-300" : "bg-white/10 text-white/50"}`} data-testid={`watch-status-${c.id}`}>{c.attendance === "present" ? "In building" : c.attendance === "checked_out" ? "Checked out" : "Not arrived"}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Button size="sm" onClick={() => mark(c.id, "arrive")} className="rounded-full bg-emerald-500 text-white text-xs" data-testid={`arrive-${c.id}`}>✔ Arrived</Button>
              <Button size="sm" onClick={() => mark(c.id, "leave")} className="rounded-full bg-slate-600 text-white text-xs" data-testid={`leave-${c.id}`}>↩ Left</Button>
              <Button size="sm" variant="outline" onClick={() => setOpenNote(openNote === c.id ? null : c.id)} className="rounded-full text-xs bg-transparent border-white/30 text-white" data-testid={`note-btn-${c.id}`}>Note</Button>
            </div>
            {openNote === c.id && <NoteBox childId={c.id} onDone={() => setOpenNote(null)} />}
          </div>
        ))}
      </main>
    </div>
  );
}
